import React, { lazy, Suspense } from "react";
import { HashRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { DataProvider, useData } from "./context/DataContext";
import { Layout } from "./components/Layout";
import { AdminLayout } from "./components/AdminLayout";
import { ErrorBoundary, PageSkeleton } from "./components/ui";

// ─── Lazy-loaded Public Pages ───────────────────────────────────────
const Home = lazy(() =>
  import("./pages/Home").then((m) => ({ default: m.Home })),
);
const Onboarding = lazy(() =>
  import("./pages/Onboarding").then((m) => ({ default: m.Onboarding })),
);
const MapPage = lazy(() =>
  import("./pages/Map").then((m) => ({ default: m.MapPage })),
);
const BlogList = lazy(() =>
  import("./pages/BlogUpdates").then((m) => ({ default: m.BlogList })),
);
const UpdatesList = lazy(() =>
  import("./pages/BlogUpdates").then((m) => ({ default: m.UpdatesList })),
);
const ModsPage = lazy(() =>
  import("./pages/Library").then((m) => ({ default: m.ModsPage })),
);
const GalleryPage = lazy(() =>
  import("./pages/Library").then((m) => ({ default: m.GalleryPage })),
);
const RulesPage = lazy(() =>
  import("./pages/Library").then((m) => ({ default: m.RulesPage })),
);
const SkinsPage = lazy(() =>
  import("./pages/Library").then((m) => ({ default: m.SkinsPage })),
);
const PlayerProfile = lazy(() =>
  import("./pages/World").then((m) => ({ default: m.PlayerProfile })),
);
const CommunityPage = lazy(() =>
  import("./pages/Community").then((m) => ({ default: m.CommunityPage })),
);
const EventsPage = lazy(() =>
  import("./pages/Events").then((m) => ({ default: m.EventsPage })),
);
const SponsorsPage = lazy(() =>
  import("./pages/Sponsors").then((m) => ({ default: m.SponsorsPage })),
);

// ─── Lazy-loaded Admin Pages ────────────────────────────────────────
const AdminLogin = lazy(() =>
  import("./pages/admin/Login").then((m) => ({ default: m.AdminLogin })),
);
const AdminDashboard = lazy(() =>
  import("./pages/admin/Dashboard").then((m) => ({
    default: m.AdminDashboard,
  })),
);
const AdminSiteSettings = lazy(() =>
  import("./pages/admin/SiteSettings").then((m) => ({
    default: m.AdminSiteSettings,
  })),
);
const ContentManager = lazy(() =>
  import("./pages/admin/ContentManager").then((m) => ({
    default: m.ContentManager,
  })),
);
const UpdateManager = lazy(() =>
  import("./pages/admin/UpdateManager").then((m) => ({
    default: m.UpdateManager,
  })),
);
const OnboardingSettings = lazy(() =>
  import("./pages/admin/OnboardingSettings").then((m) => ({
    default: m.OnboardingSettings,
  })),
);
const MapManager = lazy(() =>
  import("./pages/admin/MapManager").then((m) => ({ default: m.MapManager })),
);
const GalleryManager = lazy(() =>
  import("./pages/admin/GalleryManager").then((m) => ({
    default: m.GalleryManager,
  })),
);
const ModManager = lazy(() =>
  import("./pages/admin/ModManager").then((m) => ({ default: m.ModManager })),
);
const SkinManager = lazy(() =>
  import("./pages/admin/SkinManager").then((m) => ({ default: m.SkinManager })),
);
const RulesManager = lazy(() =>
  import("./pages/admin/RulesManager").then((m) => ({
    default: m.RulesManager,
  })),
);
const LibrarySettings = lazy(() =>
  import("./pages/admin/LibrarySettings").then((m) => ({
    default: m.LibrarySettings,
  })),
);
const AdminSecurity = lazy(() =>
  import("./pages/admin/AdminSecurity").then((m) => ({
    default: m.AdminSecurity,
  })),
);
const RegistrationManager = lazy(() =>
  import("./pages/admin/RegistrationManager").then((m) => ({
    default: m.RegistrationManager,
  })),
);
const EventManager = lazy(() =>
  import("./pages/admin/EventManager").then((m) => ({
    default: m.EventManager,
  })),
);
const RecordManager = lazy(() =>
  import("./pages/admin/RecordManager").then((m) => ({
    default: m.RecordManager,
  })),
);
const HytaleNewsSync = lazy(() =>
  import("./pages/admin/HytaleNewsSync").then((m) => ({
    default: m.HytaleNewsSync,
  })),
);
const SupporterManager = lazy(() =>
  import("./pages/admin/SupporterManager").then((m) => ({
    default: m.SupporterManager,
  })),
);
const SponsorManager = lazy(() =>
  import("./pages/admin/SponsorManager").then((m) => ({
    default: m.SponsorManager,
  })),
);

// ─── Lazy-loaded Employee Pages ─────────────────────────────────────
const EmployeeLogin = lazy(() =>
  import("./pages/employee/EmployeeLogin").then((m) => ({
    default: m.EmployeeLogin,
  })),
);
const EmployeeRegistrations = lazy(() =>
  import("./pages/employee/EmployeeRegistrations").then((m) => ({
    default: m.EmployeeRegistrations,
  })),
);

// ─── Loading Fallback ───────────────────────────────────────────────
const LoadingFallback: React.FC = () => (
  <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-white gap-4">
    <div className="w-12 h-12 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin"></div>
    <div className="font-serif text-xl tracking-widest text-emerald-500 animate-pulse">
      Carregando...
    </div>
  </div>
);

const AppInner: React.FC = () => {
  const { data, isLoading } = useData();

  // Guard: Show a loading splash screen while fetching latest data from Supabase
  // This prevents the flicker of mock data before the real data is loaded
  if (isLoading || !data || !data.siteConfig) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-white gap-4">
        <div className="w-12 h-12 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin"></div>
        <div className="font-serif text-xl tracking-widest text-emerald-500 animate-pulse">
          Sintonizando o Reino...
        </div>
        <div className="text-slate-500 text-[10px] font-bold uppercase tracking-[0.4em] opacity-40">
          Games no Linux
        </div>
      </div>
    );
  }

  return (
    <HashRouter>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Admin Routes */}
          <Route
            path="/administrador"
            element={<Navigate to="/admin" replace />}
          />
          <Route
            path="/administrador/*"
            element={<Navigate to="/admin" replace />}
          />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Navigate to="dashboard" />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="site-settings" element={<AdminSiteSettings />} />
            <Route path="updates" element={<UpdateManager />} />
            <Route path="map" element={<MapManager />} />
            <Route
              path="content"
              element={
                <ContentManager
                  title="Blog & Updates"
                  description="Gerencie postagens e notas de atualização."
                  dataKey="blogPosts"
                  fields={[
                    { name: "title", label: "Título", type: "text" },
                    { name: "author", label: "Autor", type: "text" },
                    { name: "content", label: "Conteúdo", type: "textarea" },
                    { name: "date", label: "Data", type: "text" },
                    { name: "imageUrl", label: "URL da Imagem", type: "text" },
                  ]}
                />
              }
            />
            <Route path="onboarding" element={<OnboardingSettings />} />
            <Route path="mods" element={<ModManager />} />
            <Route path="skins" element={<SkinManager />} />
            <Route path="rules" element={<RulesManager />} />
            <Route path="library-settings" element={<LibrarySettings />} />
            <Route path="gallery" element={<GalleryManager />} />
            <Route path="registrations" element={<RegistrationManager />} />
            <Route path="events" element={<EventManager />} />
            <Route path="ranking" element={<RecordManager />} />
            <Route path="supporters" element={<SupporterManager />} />
            <Route path="sponsors" element={<SponsorManager />} />
            <Route path="hytale-sync" element={<HytaleNewsSync />} />
            <Route path="security" element={<AdminSecurity />} />
          </Route>

          {/* Employee Routes */}
          <Route path="/employee/login" element={<EmployeeLogin />} />
          <Route
            path="/employee/registrations"
            element={<EmployeeRegistrations />}
          />

          {/* Public Routes */}
          <Route
            path="/"
            element={
              <Layout>
                <Home />
              </Layout>
            }
          />
          <Route
            path="/start"
            element={
              <Layout>
                <Onboarding />
              </Layout>
            }
          />

          <Route
            path="/map"
            element={
              <Layout>
                <MapPage />
              </Layout>
            }
          />

          <Route
            path="/blog"
            element={
              <Layout>
                <BlogList />
              </Layout>
            }
          />
          <Route
            path="/blog/:postSlug"
            element={
              <Layout>
                <BlogList />
              </Layout>
            }
          />
          <Route
            path="/updates"
            element={
              <Layout>
                <UpdatesList />
              </Layout>
            }
          />
          <Route
            path="/gallery"
            element={
              <Layout>
                <GalleryPage />
              </Layout>
            }
          />

          {/* Dropdown Routes */}
          <Route
            path="/community"
            element={
              <Layout>
                <CommunityPage />
              </Layout>
            }
          />
          <Route
            path="/events"
            element={
              <Layout>
                <EventsPage />
              </Layout>
            }
          />
          <Route
            path="/sponsors"
            element={
              <Layout>
                <SponsorsPage />
              </Layout>
            }
          />
          <Route
            path="/players"
            element={
              <Layout>
                <CommunityPage />
              </Layout>
            }
          />

          {/* World Items */}
          <Route
            path="/players/:playerId"
            element={
              <Layout>
                <PlayerProfile />
              </Layout>
            }
          />

          <Route
            path="/mods"
            element={
              <Layout>
                <ModsPage />
              </Layout>
            }
          />
          <Route
            path="/rules"
            element={
              <Layout>
                <RulesPage />
              </Layout>
            }
          />
          <Route
            path="/skins"
            element={
              <Layout>
                <SkinsPage />
              </Layout>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </HashRouter>
  );
};

const App: React.FC = () => {
  return (
    <HelmetProvider>
      <ErrorBoundary>
        <DataProvider>
          <AppInner />
        </DataProvider>
      </ErrorBoundary>
    </HelmetProvider>
  );
};

export default App;
